import { omit, slice, isEmpty } from 'lodash'

export const transformItemMappingModifierPreferences = (
  itemMappingPreferences = {},
  modifierGroups
) => {
  const defaultHasModifierGroupsValues = [
    {
      modifierGroupUuid: null,
      maximumCount: null,
      minimumCount: null,
      secondaryPriceThresholdCount: null,
    },
  ]

  const itemMappingPreferencesArray = Object.entries(
    itemMappingPreferences
  )

  const mergedCategoryPreferencesWithModifierUuid =
    itemMappingPreferencesArray.reduce((result, current) => {
      const modifierGroupUuid = current[0]
      const itemMappingPreferencesParams = current[1]

      const mergedModifierGroup = {
        modifierGroupUuid,
        ...itemMappingPreferencesParams,
      }
      return [...result, mergedModifierGroup]
    }, [])

  const hasModifierGroups =
    mergedCategoryPreferencesWithModifierUuid.filter(
      (modifierPreference) =>
        Object.keys(modifierPreference).includes(
          'secondaryPriceThresholdCount'
        )
    )

  const modifierGroupsUuids = modifierGroups?.map(
    (modifierGroup) => modifierGroup.uuid
  )

  // checking if modifier group from itemMappingPreferences
  // exists in modifier groups
  // if no, such itemMappingPreferences is omited
  const filteredHasModifierGroups = hasModifierGroups.filter(
    (hasModifierGroup) =>
      modifierGroupsUuids?.includes(
        hasModifierGroup.modifierGroupUuid
      )
  )

  const hasModifierGroupsWithDefaultValue =
    filteredHasModifierGroups.length === 0
      ? defaultHasModifierGroupsValues
      : filteredHasModifierGroups

  return {
    hasModifierGroups: hasModifierGroupsWithDefaultValue,
  }
}

export const transformHasAndBelongsModiferGroups = (
  modifierGroupsMerged,
  itemMappingPreferences = {}
) => {
  const { belongsToModifierGroups, hasModifierGroups } =
    modifierGroupsMerged

  const modifierGroups = [...hasModifierGroups]
  const itemMappingPreferencesEntries = Object.entries(
    itemMappingPreferences
  )
  const existingitemMappingPreferencesUuids =
    itemMappingPreferencesEntries.map(
      (itemMappingPreferencesEntry) => itemMappingPreferencesEntry[0]
    )

  const newModifierGroups = modifierGroups.filter(
    (modifierGroup) =>
      !existingitemMappingPreferencesUuids.includes(
        modifierGroup.modifierGroupUuid
      )
  )

  const newitemMappingPreferences = newModifierGroups.reduce(
    (result, current) => {
      const modifierGroup = !isEmpty(current.modifierGroupUuid)
        ? {
            [current.modifierGroupUuid]: {
              ...omit(current, 'modifierGroupUuid'),
            },
          }
        : {}

      return { ...result, ...modifierGroup }
    },
    {}
  )

  const changeditemMappingPreferences =
    itemMappingPreferencesEntries.reduce((result, current) => {
      const itemCategoryPreferenceUuid = current[0]

      const itemCategoryPreferenceChanged =
        omit(
          modifierGroups.find(
            (modifierGroup) =>
              modifierGroup.modifierGroupUuid ===
              itemCategoryPreferenceUuid
          ),
          'modifierGroupUuid'
        ) ?? {}

      const preferences = {
        [itemCategoryPreferenceUuid]: {
          ...current[1],
          ...itemCategoryPreferenceChanged,
        },
        ...result,
      }

      return preferences
    }, {})

  return {
    ...changeditemMappingPreferences,
    ...newitemMappingPreferences,
  }
}

export const filteredModifiersOptionsByTakenOptions = (
  index,
  hasModifierGroups,
  selectedHasModifierGroupsValues,
  selectedBelongsToModifierGroupsValues
) => {
  const hasModifierGroupsSelectOptions = hasModifierGroups.map(
    ({ name, uuid }) => ({
      name,
      uuid,
    })
  )

  const selectedBelongsToModifierGroups =
    selectedBelongsToModifierGroupsValues.map(
      ({ modifierGroupUuid }) => modifierGroupUuid
    )

  const selectedHasModifierGroups =
    selectedHasModifierGroupsValues.map(
      ({ modifierGroupUuid }) => modifierGroupUuid
    )

  const previousSelectedModifierGroups = slice(
    selectedHasModifierGroups,
    0,
    index
  )

  const selectedHasModifierAndBelongsTo = [
    ...previousSelectedModifierGroups,
    ...selectedBelongsToModifierGroups,
  ]

  const allOptions = hasModifierGroupsSelectOptions.filter(
    ({ uuid }) => !selectedHasModifierAndBelongsTo.includes(uuid)
  )

  return allOptions
}

const convertNumberToPercentage = (number) => {
  if (isNaN(Number(number))) {
    return 0
  }

  return Number(number) * 100
}

const convertPercentageToNumber = (percentage = 0) => {
  if (isNaN(Number(percentage))) {
    return 0
  }

  return Number(percentage) / 100
}

// We have one field rate and two radio buttons: taxableRate and taxInclusiveRate
// If taxable is true, then taxableRate is shown and taxInclusiveRate is hidden
// If taxInclusive is true, then taxInclusiveRate is shown and taxableRate is hidden
export const calculateTaxRateInitialValue = (
  taxable,
  taxInclusive,
  taxRate
) => {
  const taxRatePercentage = convertNumberToPercentage(taxRate) || null
  if (taxable) {
    return {
      taxableRate: taxRatePercentage,
      taxInclusiveRate: null,
    }
  }

  if (taxInclusive) {
    return {
      taxInclusiveRate: taxRatePercentage,
      taxableRate: null,
    }
  }
}

// We have one field rate which we send to the backend
// And in UI we have two radio buttons: taxableRate and taxInclusiveRate
// We have to decide which value to send to the backend
// based on the selected radio button
export const calculateTaxRateToSubmit = (
  taxable = false,
  taxableRate,
  taxInclusive = false,
  taxInclusiveRate
) => {
  if (taxable) {
    return convertPercentageToNumber(taxableRate)
  } else if (taxInclusive) {
    return convertPercentageToNumber(taxInclusiveRate)
  } else {
    return null
  }
}
